import { useState, useEffect } from 'react';
import { FAIcon, IconStyle, useTranslation, useSession, TextLink, TextLinkStyle } from '@nextgen-web-framework/all';
import type { AppState } from '@nextgen-web-framework/core';
import {
  updateMyList,
  myListApi,
  ListResponse,
  UserListResponse,
  getWishlist,
  isItemisInLists,
  InitialCartListState,
  type ListBundleSlot,
  getBundleRequestSlots,
  PublishWishlistEvent,
  CartEventsEnum,
  productEntryData,
  validateBundleInEntries,
  type BundleSlot,
  type ListEntry,
  updateSpecificListByUser,
  ErrorAPI,
} from '@commerce-cart-webcomponents/sdk';

import { useDispatch, useSelector } from 'react-redux';
import {
  BundleType,
  productDataTransformer,
  type IProduct,
  type ProductData,
} from '@commerce-product-webcomponents/sdk';
import {
  useCartNotification,
  TOAST_TIMEOUT,
  CartNotificationTheme,
  AddToListDrawer,
  DynamicBundleDrawer,
  getProductDataById,
  useExceptionToast,
} from '@commerce-cart-webcomponents/ui';
import { type IBundleState, getLinearBundleState } from '@commerce-product-webcomponents/ui';
import styles from './AddToListToggle.module.scss';
import { AddToListToggleProps } from './types/propsType';
import { AddToListToggleLoader } from './add-to-list-toggle-loader/AddToListToggleLoader';

export const AddToListToggle = ({
  className,
  isBundleProduct = false,
  productData,
  withText = false,
  bundleSlots = [],
  disabled,
}: AddToListToggleProps) => {
  const dispatch = useDispatch();
  const t = useTranslation();
  const { myList, listsByUser } = useSelector((state: AppState) => state.commerceMyList) as InitialCartListState;
  const [filled, setFilled] = useState<boolean>(
    !!myList?.payLoad?.entries?.find((p) => p.product.id === productData?.productId),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [showListDrawer, setShowListDrawer] = useState<boolean>(false);
  const { isABO, isMember, isRegistered } = useSession();
  const [showLoader, setShowLoader] = useState(false);
  const [showDynamicBundleDrawer, setShowDynamicBundleDrawer] = useState(false);
  const [dynamicBundleSlots, setDynamicBundleSlots] = useState<ListBundleSlot.BundleSlot[]>([]);
  const [dynamicBundleData, setDynamicBundleData] = useState<IProduct>();
  const [staticBundleSlots, setStaticBundleSlots] = useState<ListBundleSlot.BundleSlot[]>([]);
  const addToText = isABO() ? t('cart.addToListToggle.addToList') : t('cart.addToListToggle.addToWishlist');
  const addedToWishlist = t('cart.addToListToggle.ProductAddedToWishlist');
  const removeFromWishlist = t('cart.addToListToggle.ProductRemovedFromWishlist');
  const { createToast, deleteToast } = useCartNotification('addToList');
  const isDynamicBundle = productData?.bundle?.type === BundleType.Variable;
  const showSelectBundleTooltip = isBundleProduct && isDynamicBundle;
  const triggerToast = useExceptionToast();

  useEffect(() => {
    if (productData?.productId) {
      setFilled(isItemisInLists(myList, listsByUser, productData.productId));
    }
  }, [myList, listsByUser, productData?.productId]);

  const listUpdateHandler = (updatedList: UserListResponse, isRemove = false) => {
    setIsLoading(false);
    dispatch(updateMyList(getWishlist(updatedList) as ListResponse));
    //Also update all list by user to latest
    dispatch(updateSpecificListByUser(updatedList));
    createToast(
      {
        theme: CartNotificationTheme.SUCCESS,
        rightSeperator: false,
        content: isRemove ? removeFromWishlist : addedToWishlist,
        clickIcon: 'times',
        icon: 'check-circle',
        timeOut: TOAST_TIMEOUT,
      },
      deleteToast,
    );
  };

  const addToList = () => {
    if (myList?.listId) {
      setShowLoader(true);
      setIsLoading(true);
      const entriesFromCart = validateBundleInEntries([
        {
          product: {
            id: productData?.productId as string,
          },
          quantity: 1,
          bundleSlots: bundleSlots as BundleSlot[],
        },
      ]);
      myListApi
        .addToList([
          {
            version: myList.version as number,
            entries: entriesFromCart as ListEntry[],
            listId: myList?.listId,
          },
        ])
        .then((updatedList: UserListResponse) => {
          listUpdateHandler(updatedList);
          const productEntry = productEntryData(updatedList, productData?.productId || '');
          PublishWishlistEvent({
            cartEvent: CartEventsEnum.AddToWishlist,
            listEntry: productEntry,
          });
        })
        .catch((...errorProps: ErrorAPI[]) => {
          setIsLoading(false);
          triggerToast(errorProps);
        })
        .finally(() => {
          setShowLoader(false);
        });
    }
  };

  const removeEntryFromList = () => {
    setShowLoader(true);
    setIsLoading(true);

    const removingEntry = myList?.payLoad?.entries?.find((entry) => entry.product.id === productData?.productId);

    if (removingEntry && removingEntry.entryId) {
      myListApi
        .removeEntryFromList({
          entryId: [removingEntry.entryId],
          listId: myList?.listId,
        })
        .then((updatedList: ListResponse) => {
          listUpdateHandler({ lists: [updatedList] } as UserListResponse, true);
          PublishWishlistEvent({
            cartEvent: CartEventsEnum.RemoveWishlistEntry,
            listEntry: removingEntry,
          });
        })
        .catch((...errorProps: ErrorAPI[]) => {
          setIsLoading(false);
          triggerToast(errorProps);
        })
        .finally(() => {
          setShowLoader(false);
        });
    }
  };

  const showHeartIconFilled = (value: boolean) => {
    setShowListDrawer(false);
    setFilled(value);
  };

  const handler = (filled: boolean) => {
    if (!filled) {
      addToList();
    }
    // Remove Scenario is only for Customer
    if (filled && !isABO() && !isMember()) {
      removeEntryFromList();
    }
    setFilled(!filled);
  };

  const onListDrawerClose = () => {
    setShowListDrawer(false);
    setDynamicBundleSlots([]);
  };

  const onAddToListToggleClick = async (e?: React.KeyboardEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (showSelectBundleTooltip) {
      const productInfo = await getProductDataById(productData?.productId);
      if (!(productInfo instanceof Response)) {
        setDynamicBundleData(productDataTransformer(productInfo));
        setShowDynamicBundleDrawer(true);
      }
      return;
    }

    if (productData?.bundle?.type === BundleType.Fixed) {
      getProductDataById(productData.productId).then((res: ProductData | Response) => {
        if (!(res instanceof Response)) {
          const transformedData = productDataTransformer(res);
          const bundleState = getLinearBundleState(transformedData.bundle);
          const bundleSlotsDerived = getBundleRequestSlots(bundleState);
          setStaticBundleSlots(bundleSlotsDerived);
        }
      });
    }

    if (!isABO() && !isMember()) {
      if (!isLoading) {
        handler(filled);
      }
    } else {
      if (!disabled) {
        setShowListDrawer(true);
      }
    }
  };

  const handleDynamicDrawerConfirmButton = (bundleState: IBundleState[]) => {
    const bundleSlots = getBundleRequestSlots(bundleState);
    setDynamicBundleSlots(bundleSlots);
    setShowListDrawer(true);
    setShowDynamicBundleDrawer(false);
  };

  const handleDynamicDrawerClose = () => {
    setShowDynamicBundleDrawer(false);
    setDynamicBundleSlots([]);
  };

  const getBundleSlots = () => {
    if (staticBundleSlots.length) {
      return staticBundleSlots;
    }

    if (dynamicBundleSlots.length) {
      return dynamicBundleSlots;
    }
    return bundleSlots;
  };

  return (
    <>
      {showDynamicBundleDrawer && (
        <DynamicBundleDrawer
          onClose={handleDynamicDrawerClose}
          onConfirmButtonClick={handleDynamicDrawerConfirmButton}
          productData={dynamicBundleData}
        />
      )}
      {showListDrawer && (
        <AddToListDrawer
          bundleSlots={getBundleSlots()}
          onDrawerClose={onListDrawerClose}
          heartIconFilledcb={showHeartIconFilled}
          productData={productData}
        />
      )}

      {isRegistered() && showLoader ? (
        <div className={`${styles['heart-container']} ${className}`}>
          <AddToListToggleLoader />
        </div>
      ) : (
        <div
          role="presentation"
          className={`${styles['heart-container']} ${className} ${withText ? styles['with-text'] : ''}`}
          onClick={onAddToListToggleClick}
          onKeyDown={onAddToListToggleClick}
          data-testid="heart-icon"
        >
          <FAIcon iconName="heart" iconStyle={filled ? IconStyle.SOLID : IconStyle.REGULAR} />
          {withText ? (
            <TextLink
              text={addToText}
              textLinkStyle={TextLinkStyle.UNDERLINE}
              onClick={(e) => {
                e.preventDefault();
                onAddToListToggleClick();
              }}
            />
          ) : (
            ''
          )}
        </div>
      )}
    </>
  );
};
